import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Spinner
} from '@fluentui/react-components';
import '../styles/PlanPage.css';
import CoralShellColumn from '../coral/components/Layout/CoralShellColumn';
import CoralShellRow from '../coral/components/Layout/CoralShellRow';
import Content from '../coral/components/Content/Content';
import HomeInput from '@/components/content/HomeInput';
import { NewTaskService } from '../services/NewTaskService';
import PlanPanelLeft from '@/components/content/PlanPanelLeft';
import ContentToolbar from '@/coral/components/Content/ContentToolbar';
import { TeamConfig } from '../models/Team';
import { TeamService } from '../services/TeamService';
import InlineToaster, { useInlineToaster } from "../components/toast/InlineToaster";

/**
 * HomePage component - displays task lists and provides navigation
 * Accessible via the route "/"
 */
const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast, dismissToast } = useInlineToaster();
    const [selectedTeam, setSelectedTeam] = useState<TeamConfig | null>(null);
    const [isLoadingTeam, setIsLoadingTeam] = useState<boolean>(true);
    const [reloadLeftList, setReloadLeftList] = useState<boolean>(true);

    // Reset reload function for PlanPanelLeft
    const resetReload = useCallback(() => {
        setReloadLeftList(false);
    }, []);

    // Domain to team mapping for filtering
    const domainTeamMapping: Record<string, string[]> = {
        'finance': [
            'ap_operations_team',
            'ap_operations_team_lite', 
            'ar_operations_team',
            'expense_management_team',
            'fpa_team',
            'gl_close_team',
            'procurement_team',
            'tax_compliance_team',
            'treasury_team'
        ],
        'order-management': [
            'order_operations_team'
        ]
    };

    useEffect(() => {
        const initTeam = async () => {
            setIsLoadingTeam(true);

            try {
                // Get selected domain from sessionStorage
                const selectedDomain = sessionStorage.getItem('selectedDomain');
                console.log('Selected domain:', selectedDomain);

                // Fetch all teams first
                const allTeams = await TeamService.getUserTeams();
                
                // Filter teams based on domain
                let availableTeams = allTeams;
                if (selectedDomain && domainTeamMapping[selectedDomain]) {
                    const allowedTeamIds = domainTeamMapping[selectedDomain];
                    availableTeams = allTeams.filter(team => 
                        allowedTeamIds.includes(team.team_id)
                    );
                    console.log('Filtered teams for domain:', availableTeams.map(t => t.name));
                }

                // Pick the first available team for this domain
                if (availableTeams.length > 0) {
                    const defaultTeam = availableTeams[0];
                    
                    // Select this team via the backend
                    const selectResult = await TeamService.selectTeam(defaultTeam.team_id);
                    
                    if (selectResult.success) {
                        // Now initialize with the selected team
                        const initResponse = await TeamService.initializeTeam();
                        
                        if (initResponse.data?.status === 'Request started successfully') {
                            setSelectedTeam(defaultTeam);
                            TeamService.storageTeam(defaultTeam);
                            
                            console.log('Team loaded successfully:', defaultTeam.name);
                            showToast(
                                `${defaultTeam.name} initialized with ${defaultTeam.agents?.length || 0} agents`,
                                "success"
                            );
                        }
                    } else {
                        // Fallback: just use the team without backend init
                        setSelectedTeam(defaultTeam);
                        TeamService.storageTeam(defaultTeam);
                        showToast(
                            `${defaultTeam.name} loaded`,
                            "success"
                        );
                    }
                } else {
                    // No teams available for this domain
                    console.log('No teams available for domain:', selectedDomain);
                    showToast("No teams available for this domain", "warning");
                }

            } catch (error) {
                console.error('Error initializing team from backend:', error);
                showToast("Team initialization failed", "warning");
            } finally {
                setIsLoadingTeam(false);
            }
        };

        initTeam();
    }, []);

    /**
    * Handle new task creation from the "New task" button
    * Resets textarea to empty state on HomePage
    */
    const handleNewTaskButton = useCallback(() => {
        NewTaskService.handleNewTaskFromHome();
    }, []);

    /**
     * Handle team selection from the Settings button
     */
    const handleTeamSelect = useCallback(async (team: TeamConfig | null) => {
        setSelectedTeam(team);
        setReloadLeftList(true);
        console.log('handleTeamSelect called with team:', true);
        if (team) {

            try {
                setIsLoadingTeam(true);
                const initResponse = await TeamService.initializeTeam(true);

                if (initResponse.data?.status === 'Request started successfully' && initResponse.data?.team_id) {
                    console.log('handleTeamSelect:', initResponse.data?.team_id);

                    // Now fetch the actual team details using the team_id
                    const teams = await TeamService.getUserTeams();
                    const initializedTeam = teams.find(team => team.team_id === initResponse.data?.team_id);

                    if (initializedTeam) {
                        setSelectedTeam(initializedTeam);
                        TeamService.storageTeam(initializedTeam);
                        setReloadLeftList(true)
                        console.log('Team loaded successfully handleTeamSelect:', initializedTeam.name);
                        console.log('Team agents handleTeamSelect:', initializedTeam.agents?.length || 0);

                        // Show toast only here - after team is fully initialized
                        showToast(
                            `${initializedTeam.name} selected with ${initializedTeam.agents?.length || 0} agents`,
                            "success"
                        );
                    }

                } else {
                    throw new Error('Invalid response from init_team endpoint');
                }
            } catch (error) {
                console.error('Error setting current team:', error);
                showToast("Failed to switch team", "warning");
            } finally {
                setIsLoadingTeam(false);
            }

            // Toast removed from here - moved inside the success block above to avoid duplicates
        } else {
            showToast(
                "No team is currently selected",
                "info"
            );
        }
    }, [showToast, setReloadLeftList]);


    /**
     * Handle team upload completion - refresh team list and keep Business Operations Team as default
     */
    const handleTeamUpload = useCallback(async () => {
        try {
            const teams = await TeamService.getUserTeams();
            console.log('Teams refreshed after upload:', teams.length);

            if (teams.length > 0) {
                // Always keep "Human Resources Team" as default, even after new uploads
                const hrTeam = teams.find(team => team.name === "Human Resources Team");
                const defaultTeam = hrTeam || teams[0];
                setSelectedTeam(defaultTeam);
                console.log('Default team after upload:', defaultTeam.name);
                console.log('Human Resources Team remains default');
                showToast(
                    `Team uploaded successfully! ${defaultTeam.name} remains your default team.`,
                    "success"
                );
            }
        } catch (error) {
            console.error('Error refreshing teams after upload:', error);
        }
    }, [showToast]);


    return (
        <>
            <InlineToaster />
            <CoralShellColumn>
                <CoralShellRow>
                    <PlanPanelLeft
                        reloadTasks={reloadLeftList}
                        onNewTaskButton={handleNewTaskButton}
                        restReload={resetReload}
                        onTeamSelect={handleTeamSelect}
                        onTeamUpload={handleTeamUpload}
                        isHomePage={true}
                        selectedTeam={selectedTeam}
                    />
                    <Content>
                        <ContentToolbar
                            panelTitle={"Exception Workspace"}
                        ></ContentToolbar>
                        {!isLoadingTeam ? (
                            <HomeInput
                                selectedTeam={selectedTeam}
                            />
                        ) : (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '200px'
                            }}>
                                <Spinner label="Loading team configuration..." />
                            </div>
                        )}
                    </Content>

                </CoralShellRow>
            </CoralShellColumn>
        </>
    );
};

export default HomePage;